import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, { buildFetchUrl, buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import { APIGuestRequestFromJSON } from '../../../generated-types/models/APIGuestRequest'
import { APIGuestRequestRequest } from '../../../generated-types/models/APIGuestRequestRequest'
import { APIPatchedGuestRequestRequest } from '../../../generated-types/models/APIPatchedGuestRequestRequest'
import { APIPaginatedGuestRequestList } from '../../../generated-types/models/APIPaginatedGuestRequestList'
import { GuestRequestrequestApi } from '../../../generated-types/apis/GuestRequestrequestApi'
import { GuestRequestpartApi } from '../../../generated-types/apis/GuestRequestpartApi'
import { GuestRequestrequestFileApi } from '../../../generated-types/apis/GuestRequestrequestFileApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createFileInfo, createUser, getPKfromSelf, normalizeEditors, parseNestedObject, parseNestedObjectList } from '../../../utils/functions'
import { createArticle } from '../../Item/api/articleApi'
import { type GuestRequest } from '../utils/types'
import { PaginatedQueryParameters } from "../../../api/hooks/utils/types";
import { GuestRequestFilters } from "../hooks/useGuestRequestQueries";
import { EntitySearchResponse } from "../../../hooks/useEntitySearch";

const log = new LogTool({ context: 'GuestRequestAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instances
 */
function getGuestRequestsApi(): GuestRequestrequestApi {
  return new GuestRequestrequestApi(getApiConfiguration())
}

function getGuestRequestFilesApi(): GuestRequestrequestFileApi {
  return new GuestRequestrequestFileApi(getApiConfiguration())
}

function getGuestPartsApi(): GuestRequestpartApi {
  return new GuestRequestpartApi(getApiConfiguration())
}

/**
 * Create GuestRequest from API response
 */
export function createGuestRequest(response: unknown): GuestRequest {
  const rawResponse =
    typeof response === 'object' && response !== null
      ? response as Record<string, unknown>
      : {}

  const apiGuestRequest = APIGuestRequestFromJSON(rawResponse)
  const {
    read: _read,
    files: _files,
    editors: editorsRaw,
    createdAt: _createdAt,
    ...guestRequestBase
  } = apiGuestRequest

  const articlesSource =
    ('articles' in rawResponse ? rawResponse.articles : undefined) ??
    ('article' in rawResponse ? rawResponse.article : undefined)

  let articles: GuestRequest['articles']
  if (Array.isArray(articlesSource)) {
    const parsedList = parseNestedObjectList(articlesSource, createArticle)
    articles = Array.isArray(parsedList) ? parsedList : parsedList
  } else if (articlesSource !== undefined) {
    const parsedArticle = parseNestedObject(articlesSource, createArticle)
    articles =
      typeof parsedArticle === 'string'
        ? parsedArticle
        : parsedArticle
          ? [parsedArticle]
          : undefined
  }

  const filesParsed = parseNestedObjectList(rawResponse.files, createFileInfo)
  const files: GuestRequest['files'] = Array.isArray(filesParsed)
    ? filesParsed
    : typeof filesParsed === 'string'
      ? filesParsed
      : []

  const editorsParsed = parseNestedObjectList(rawResponse.editors, createUser)
  const editors: GuestRequest['editors'] = Array.isArray(editorsParsed)
    ? editorsParsed
    : typeof editorsParsed === 'string'
      ? editorsParsed
      : []

  // Ensure createdAt is a valid Date object
  // APIGuestRequestFromJSON may create an Invalid Date if the date string is malformed
  let createdAt: Date
  if (apiGuestRequest.createdAt instanceof Date) {
    // Check if it's a valid date (Invalid Date objects still pass instanceof Date check)
    createdAt = isNaN(apiGuestRequest.createdAt.getTime()) 
      ? new Date() 
      : apiGuestRequest.createdAt
  } else if (typeof apiGuestRequest.createdAt === 'string') {
    const parsedDate = new Date(apiGuestRequest.createdAt)
    createdAt = isNaN(parsedDate.getTime()) ? new Date() : parsedDate
  } else if (rawResponse.created_at && typeof rawResponse.created_at === 'string') {
    const parsedDate = new Date(rawResponse.created_at)
    createdAt = isNaN(parsedDate.getTime()) ? new Date() : parsedDate
  } else {
    // Fallback to current date if no valid date is found
    createdAt = new Date()
  }

  const guestRequest: GuestRequest = {
    ...guestRequestBase,
    key: getPKfromSelf(apiGuestRequest.self),
    read: Boolean(rawResponse.read ?? apiGuestRequest.read),
    createdAt,
    files,
    editors,
  }

  if (articles !== undefined) {
    guestRequest.articles = articles
  }

  return guestRequest
}

/**
 * Convert form input to API JSON
 */
export function inputToAPIGuestRequestJSON(input: any): Partial<APIGuestRequestRequest> {
  // Use normalizeEditors to extract self URLs from user objects
  const editors = normalizeEditors(input?.editors)
  
  // Convert editor UUIDs to full URLs if needed
  // normalizeEditors already extracts the 'self' values from user objects
  const editorUrls = editors?.map((editor: any) => {
    // If it's already any kind of URL path (absolute or relative), return as-is
    if (editor.includes('/')) {
      return editor
    }
    // Only if it's a plain UUID (no slashes), convert to full URL
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/'
    const baseUrl = envApiUrl.endsWith('/') ? envApiUrl : envApiUrl + '/'
    return `${baseUrl}v1/accounts/users/${editor}/`
  })

  const stringOrNull = (value: any) => {
    if (value === null || value === undefined) return undefined
    const asString = String(value).trim()
    return asString.length > 0 ? asString : undefined
  }

  return {
    ...(input.article && { article: input.article }),
    ...(input.remarks && { remarks: input.remarks }),
    ...(input.status && { status: input.status }),
    ...(stringOrNull(input.name) && { name: stringOrNull(input.name) }),
    ...(stringOrNull(input.email) && { email: stringOrNull(input.email) }),
    ...(stringOrNull(input.phone) && { phone: stringOrNull(input.phone) }),
    ...(stringOrNull(input.company) && { company: stringOrNull(input.company) }),
    ...(stringOrNull(input.address) && { address: stringOrNull(input.address) }),
    ...(stringOrNull(input.zipCode) && { zip_code: stringOrNull(input.zipCode) }),
    ...(stringOrNull(input.city) && { city: stringOrNull(input.city) }),
    ...(stringOrNull(input.country) && { country: stringOrNull(input.country) }),
    ...(stringOrNull(input.quantity) && { quantity: stringOrNull(input.quantity) }),
    ...(stringOrNull(input.material) && { material: stringOrNull(input.material) }),
    ...(stringOrNull(input.message) && { message: stringOrNull(input.message) }),
    ...(input.requestedBy && { requested_by: input.requestedBy }),
    ...(editorUrls && editorUrls.length >= 0 && { editors: editorUrls }),
  }
}

export async function fetchGuestRequests(
  options: PaginatedQueryParameters<GuestRequest, GuestRequestFilters> = {},
  ids?: number[]
): Promise<PaginatedResponse<GuestRequest>>
{
  log.info('Fetching GuestRequests with options -> ', options)

  // Always use apiClient to support expand parameter for files
  // The generated API doesn't support expand, so we need to use apiClient
  let url = buildApiUrl('v1/guest_request/requests/', options, ids)
  
  // Add expand=files to ensure files are included in the response
  if (!url.includes('expand=')) {
    if (url.includes('?')) url += '&'
    else url += '?'
    url += 'expand=files'
  }
  
  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)
  return {
    ...response,
    results: response.results?.map(createGuestRequest) ?? [],
  }
}

export async function searchGuestRequests(
  options: PaginatedQueryParameters<GuestRequest, GuestRequestFilters> = {},
): Promise<any> { // TODO: switch any for GuestRequestSearchResult after merging development
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = { ...(options.filters || {}) }

  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''
  
  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }
  
  const url = buildApiUrl(
    'v1/search/guest-requests/guest_requests/',
    { filters: searchFilters }
  )
  return apiClient.get<EntitySearchResponse<GuestRequest>>(url)
}

/**
 * Fetch paginated guest requests
 */
export async function deprecatedFetchGuestRequests(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<GuestRequest>> {
  log.info('Fetching guest requests with options:', options)

  const params = buildPaginationParams(options)
  const url = buildFetchUrl('v1/guest_request/requests/', params)
  const response = await apiClient.get<PaginatedResponse<Record<string, unknown>>>(url)

  const guestRequests = response.results ? response.results.map(createGuestRequest) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: guestRequests,
  }
}

/**
 * Fetch a single guest request
 */
export async function fetchGuestRequest(
  idOrUrl: string | number,
  expand?: string[]
): Promise<GuestRequest> {
  log.info('Fetching guest request:', idOrUrl)

  // If expand is needed, use apiClient as generated API doesn't support expand parameter
  if (expand && expand.length > 0) {
    const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/guest_request/requests/${idOrUrl}/`
      : idOrUrl

    const params = [`expand=${expand.join(',')}`]
    const fetchUrl = `${url}?${params.join('&')}`

    const response = await apiClient.get<any>(fetchUrl)
    return createGuestRequest(response)
  }

  const id = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? (typeof idOrUrl === 'number' ? idOrUrl : Number(idOrUrl))
    : Number(getPKfromSelf(idOrUrl))

  const response = await getGuestRequestsApi().v1GuestRequestRequestsRetrieve({ id })
  return createGuestRequest(response)
}

/**
 * Create a new guest request
 */
export async function createGuestRequestApi(
  requestData: any,
  files: File[] = []
): Promise<GuestRequest> {
  log.info('Creating guest request:', requestData)

  const json = inputToAPIGuestRequestJSON(requestData) as APIGuestRequestRequest
  const response = await getGuestRequestsApi().v1GuestRequestRequestsCreate({
    aPIGuestRequestRequest: json
  })

  const createdGuestRequest = createGuestRequest(response)

  // Upload files if provided
  const uploadPromises =
    Array.isArray(files) && files.length > 0
      ? files.map(file => {
          const requestPk = getPKfromSelf(createdGuestRequest.self)
          return getGuestRequestFilesApi().v1GuestRequestRequestsFilesCreate({
            requestPk: String(requestPk),
            guestRequest: createdGuestRequest.self,
            key: file.name,
            document: file,
          })
        })
      : []

  if (uploadPromises.length > 0) {
    try {
      await Promise.all(uploadPromises)
    } catch (error) {
      log.error('Failed to upload guest request files:', error)
    }
  }

  return createdGuestRequest
}

/**
 * Update a guest request
 */
export async function updateGuestRequestApi(
  idOrSelf: string | number,
  requestData: any
): Promise<GuestRequest> {
  log.info('Updating guest request:', idOrSelf)

  const id = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? (typeof idOrSelf === 'number' ? idOrSelf : Number(idOrSelf))
    : Number(getPKfromSelf(idOrSelf))

  const json = inputToAPIGuestRequestJSON(requestData) as APIGuestRequestRequest
  const response = await getGuestRequestsApi().v1GuestRequestRequestsUpdate({
    id,
    aPIGuestRequestRequest: json
  })

  return createGuestRequest(response)
}

/**
 * Patch a guest request
 */
export async function patchGuestRequestApi(
  idOrSelf: string | number,
  requestData: Partial<GuestRequest>
): Promise<GuestRequest> {
  log.info('Patching guest request:', idOrSelf)

  const id = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? (typeof idOrSelf === 'number' ? idOrSelf : Number(idOrSelf))
    : Number(getPKfromSelf(idOrSelf))

  const json = inputToAPIGuestRequestJSON(requestData) as APIPatchedGuestRequestRequest
  const response = await getGuestRequestsApi().v1GuestRequestRequestsPartialUpdate({
    id,
    aPIPatchedGuestRequestRequest: json
  })

  return createGuestRequest(response)
}

/**
 * Delete a guest request
 */
export async function deleteGuestRequestApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting guest request:', idOrSelf)

  const id = typeof idOrSelf === 'number' || (typeof idOrSelf === 'string' && !idOrSelf.includes('/'))
    ? (typeof idOrSelf === 'number' ? idOrSelf : Number(idOrSelf))
    : Number(getPKfromSelf(idOrSelf))

  await getGuestRequestsApi().v1GuestRequestRequestsDestroy({ id })
}

/**
 * Delete multiple guest requests
 */
export async function deleteGuestRequestsApi(requests: (string | number)[]): Promise<void> {
  log.info('Deleting guest requests:', requests)
  const deletePromises = requests.map(deleteGuestRequestApi)
  await Promise.all(deletePromises)
}


